import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useUi } from '@/store/ui';

export type DictationPhase = 'idle' | 'recording' | 'transcribing';

/**
 * Dictation for the AI composers: hold a recording, hand back its words.
 *
 * The web counterpart of the phone's `useDictation`, and deliberately the same
 * bargain: the clip is a keystroke substitute rather than content, so it goes
 * to the server, comes back as text, and is stored nowhere. The words land in
 * the draft instead of being sent, so what reaches a model is always something
 * the speaker has read first.
 *
 * Written here rather than lifted out of `ChatComposer` because a voice note
 * and a dictation want opposite things from the same API: a voice note keeps
 * the audio and uploads it as an attachment, while this throws the audio away
 * and keeps only the transcript.
 */

/**
 * MediaRecorder defaults to WebM/Opus in Chromium, which the transcription
 * provider handles but iOS cannot produce — the same preference the voice-note
 * composer settled on, for the same reason.
 */
function preferredRecordingType(): MediaRecorderOptions | undefined {
  const candidates = ['audio/mp4;codecs=mp4a.40.2', 'audio/mp4', 'audio/aac'];
  for (const mimeType of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(mimeType)) {
      return { mimeType };
    }
  }
  return undefined;
}

function extensionFor(mimeType: string): string {
  if (mimeType.includes('mp4') || mimeType.includes('aac')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  return 'webm';
}

export function useDictation(onTranscript: (text: string) => void) {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef(0);
  /* Read inside `onstop`, which is installed before the keep/discard decision
     is made — so the decision has to reach it through a ref, not a closure. */
  const keepRef = useRef(true);
  const transcriptRef = useRef(onTranscript);
  transcriptRef.current = onTranscript;

  const [phase, setPhase] = useState<DictationPhase>('idle');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Drops the mic. Leaving a track live keeps the browser's recording dot on. */
  const release = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  // Unmounting mid-recording must still release the microphone.
  useEffect(() => release, [release]);

  const start = useCallback(async () => {
    if (recorderRef.current) return;
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream, preferredRecordingType());
      chunksRef.current = [];
      keepRef.current = true;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        const durationMs = Date.now() - startedAtRef.current;
        const type = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        release();

        // Too short to hold a word, or thrown away on purpose.
        if (!keepRef.current || blob.size === 0 || durationMs < 500) {
          setPhase('idle');
          return;
        }

        setPhase('transcribing');
        const form = new FormData();
        form.append('audio', blob, `dictation.${extensionFor(type)}`);
        form.append('durationMs', String(durationMs));
        // Which language to expect. The shell's own setting rather than the
        // browser's locale: switching the app to Arabic is a deliberate act by
        // this person, where `navigator.language` is whatever the machine was
        // set up with and says nothing about who is now speaking into it.
        form.append('language', useUi.getState().language);
        api
          .upload<{ transcript: string }>('/assistant/dictations', form)
          .then(({ transcript }) => transcriptRef.current(transcript))
          .catch((cause: unknown) =>
            setError(
              cause instanceof Error ? cause.message : 'Could not transcribe that. Try again.',
            ),
          )
          .finally(() => setPhase('idle'));
      };

      recorder.start();
      recorderRef.current = recorder;
      startedAtRef.current = Date.now();
      setSeconds(0);
      setPhase('recording');
      timerRef.current = setInterval(() => setSeconds((value) => value + 1), 1000);
    } catch (cause) {
      // A denied permission or a mic another tab already holds lands here;
      // without this the promise rejects unhandled and the button does nothing.
      release();
      setPhase('idle');
      setError(
        cause instanceof Error && cause.name === 'NotAllowedError'
          ? 'Microphone access was blocked for this site.'
          : 'Could not start recording. The microphone may be in use.',
      );
    }
  }, [release]);

  const stop = useCallback((keep: boolean) => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    keepRef.current = keep;
    if (!keep) setPhase('idle');
    // The upload happens in `onstop`, which fires once the last chunk lands.
    recorder.stop();
  }, []);

  return { phase, seconds, error, start, stop };
}
